import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, Shield, X } from 'lucide-react';
import type { TeamMember } from '../../hooks/useTeamData';
import './MemberList.css';

interface MemberListProps {
  members: TeamMember[];
  isLoading: boolean;
  currentUserEmail: string;
  onRemove: (id: number, email: string, role: string) => void;
}

function MemberAvatar({ email }: { email: string }) {
  return (
    <div className="member-avatar">
      {email.charAt(0).toUpperCase()}
    </div>
  );
}

import { MemberRowSkeleton } from '@/features/admin/components/loaders/MemberSkeletons';

export function MemberList({ members, isLoading, currentUserEmail, onRemove }: MemberListProps) {
  return (
    <div id="tour-member-list" className="member-list">
      {isLoading ? (
        <>
          <MemberRowSkeleton />
          <MemberRowSkeleton />
          <MemberRowSkeleton />
        </>
      ) : members.length === 0 ? (
        <div className="member-list__empty">
          <p>No members found.</p>
        </div>
      ) : (
        <AnimatePresence>
          {members.map((member, idx) => (
            <motion.div
              key={member.id}
              className={`member-row ${idx < members.length - 1 ? 'member-row--bordered' : ''}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25, delay: idx * 0.04 }}
            >
              <div className="member-row__left">
                <MemberAvatar email={member.email} />
                <div className="member-row__info">
                  <div className="member-row__name-row">
                    <span className="member-row__email">{member.email}</span>
                    {member.role === 'Admin' && (
                      <span className="member-row__admin-badge">
                        <Shield size={10} />
                        Admin
                      </span>
                    )}
                  </div>
                  <span className="member-row__date">Added {member.date}</span>
                </div>
              </div>

              <div className="member-row__right">
                {member.status === 'active' ? (
                  <span className="member-status member-status--active">
                    <CheckCircle2 size={13} />
                    Active
                  </span>
                ) : (
                  <span className="member-status member-status--pending">
                    <Clock size={13} />
                    Pending
                  </span>
                )}

                {member.email !== currentUserEmail && (
                  <button
                    className="member-remove-btn"
                    onClick={() => onRemove(member.id, member.email, member.role)}
                    title={member.status === 'pending' ? 'Revoke Invitation' : 'Remove Member'}
                  >
                    <X size={13} />
                    {member.status === 'pending' ? 'Revoke' : 'Remove'}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      )}
    </div>
  );
}
